import { Controller } from "@hotwired/stimulus"

const STICKY_SELECT_CLASS = "fanword-select--in-sticky-column"

// Keeps the first N table columns fixed during horizontal scrolling.
// Works with TableComponent's declarative mode by marking cells with:
//   - `fanword-table__sticky-left`
// and dynamically setting `left` offsets via measured header widths.
export default class extends Controller {
  static values = { stickyLeftColumns: Number }

  connect() {
    this.resizeTimeout = null
    this.openStickySelect = null
    this.handleResize = this.handleResize.bind(this)
    this.handleTableScroll = this.handleTableScroll.bind(this)
    this.handleStickySelectPointerDown = this.handleStickySelectPointerDown.bind(this)

    this.updateStickyColumns()
    this.markStickySelects()
    window.addEventListener("resize", this.handleResize)
    this.element.addEventListener("scroll", this.handleTableScroll)
    this.element.addEventListener("pointerdown", this.handleStickySelectPointerDown, true)

    const tbody = this.element.querySelector("tbody")
    if (tbody) {
      this.rowObserver = new MutationObserver(() => {
        this.updateStickyColumns()
        this.markStickySelects()
      })
      this.rowObserver.observe(tbody, { childList: true, subtree: true, attributes: true, attributeFilter: ["id"] })
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.element.removeEventListener("scroll", this.handleTableScroll)
    this.element.removeEventListener("pointerdown", this.handleStickySelectPointerDown, true)
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout)
    this.rowObserver?.disconnect()
  }

  handleResize() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout)

    // Debounce to avoid repeated layout thrashing while resizing.
    this.resizeTimeout = setTimeout(() => {
      this.updateStickyColumns()
      this.markStickySelects()
      this.syncOpenStickySelectPicker()
    }, 50)
  }

  handleTableScroll() {
    this.syncOpenStickySelectPicker()
  }

  handleStickySelectPointerDown(event) {
    const select = event.target.closest("select")
    if (!(select instanceof HTMLSelectElement)) return
    if (!select.classList.contains(STICKY_SELECT_CLASS)) return

    this.openStickySelect = select
    select.classList.remove("fanword-select--picker-hidden")
    this.positionOneStickySelectPicker(select)
  }

  markStickySelects() {
    this.element.querySelectorAll(".fanword-table__sticky-left select").forEach((select) => {
      select.classList.add(STICKY_SELECT_CLASS)
    })
  }

  // Only the open picker is moved or closed. IntersectionObserver uses the
  // in-flow box, so sticky cells look "gone" on any scroll even while painted.
  syncOpenStickySelectPicker() {
    if (!this.openStickySelect) return

    if (this.selectIsVisibleInTable(this.openStickySelect)) {
      this.positionOneStickySelectPicker(this.openStickySelect)
    } else {
      this.hideStickySelectPicker(this.openStickySelect)
      this.openStickySelect = null
    }
  }

  // Customizable select pickers are a ::picker(select) pseudo-element, styled
  // from this class. Measure the painted select so the panel stays lined up
  // with sticky columns instead of the scrolled in-flow box.
  positionOneStickySelectPicker(select) {
    const selectRectangle = select.getBoundingClientRect()
    select.style.setProperty("--fanword-select-picker-top", `${selectRectangle.bottom}px`)
    select.style.setProperty("--fanword-select-picker-left", `${selectRectangle.left}px`)
  }

  // Chrome ignores display:none on ::picker(select). The HTML spec requires the
  // picker to close when the select is not being rendered, so briefly hide it.
  hideStickySelectPicker(select) {
    if (select.classList.contains("fanword-select--picker-hidden")) return

    select.classList.add("fanword-select--picker-hidden")
    select.blur()

    const previousDisplay = select.style.display
    select.style.display = "none"
    select.offsetHeight
    select.style.display = previousDisplay
  }

  visibleTableBounds() {
    const wrapperRectangle = this.element.getBoundingClientRect()
    const headerCell = this.element.querySelector("thead.fanword-table__sticky-header th, thead.fanword-table__sticky-header td")
    const visibleTop = headerCell ? headerCell.getBoundingClientRect().bottom : wrapperRectangle.top
    let visibleBottom = wrapperRectangle.bottom

    // Other nomination tabs also render a .floating-footer. A hidden one can
    // report top: 0 and make every select look out of view.
    const footerContainer = this.element.closest(".floating-footer-container")
    const floatingFooter = footerContainer?.querySelector(".floating-footer")
    if (floatingFooter) {
      const footerRectangle = floatingFooter.getBoundingClientRect()
      const footerOverlapsTable = footerRectangle.height > 0 &&
        footerRectangle.top < wrapperRectangle.bottom &&
        footerRectangle.bottom > wrapperRectangle.top

      if (footerOverlapsTable) {
        visibleBottom = footerRectangle.top
      }
    }

    return {
      top: visibleTop,
      bottom: visibleBottom,
      left: wrapperRectangle.left,
      right: wrapperRectangle.right
    }
  }

  selectIsVisibleInTable(select) {
    const visibleBounds = this.visibleTableBounds()
    const control = select.closest(".select-container") || select
    const controlRectangle = control.getBoundingClientRect()
    const overlappingHeight = Math.min(controlRectangle.bottom, visibleBounds.bottom) - Math.max(controlRectangle.top, visibleBounds.top)
    const overlappingWidth = Math.min(controlRectangle.right, visibleBounds.right) - Math.max(controlRectangle.left, visibleBounds.left)

    const visibleTableHeight = Math.max(visibleBounds.bottom - visibleBounds.top, 0)
    const pickerHeightBuffer = Math.min(this.estimatedPickerHeight(select), visibleTableHeight * 0.4)
    const pickerFitsBelowSelect = (controlRectangle.bottom + pickerHeightBuffer) <= visibleBounds.bottom

    return overlappingHeight > 8 && overlappingWidth > 8 && pickerFitsBelowSelect
  }

  estimatedPickerHeight(select) {
    const optionCount = select.options ? select.options.length : 4
    const boundedOptionCount = Math.min(Math.max(optionCount, 1), 6)
    const optionHeight = 38
    const pickerPadding = 20

    return boundedOptionCount * optionHeight + pickerPadding
  }

  updateStickyColumns() {
    const stickyCount = this.stickyLeftColumnsValue || 0
    if (stickyCount <= 0) return

    const table = this.element.querySelector("table")
    if (!table) return

    const headerRows = Array.from(table.querySelectorAll("thead tr"))
    if (headerRows.length === 0) return

    // Measure widths from the last header row (column labels), which is the most
    // reliable row when thead has multiple rows (e.g. tennis singles/doubles).
    const measureRow = headerRows[headerRows.length - 1]
    const measureStickyCells = Array.from(
      measureRow.querySelectorAll("th.fanword-table__sticky-left, td.fanword-table__sticky-left")
    )

    if (measureStickyCells.length === 0) return

    const headerCells = measureStickyCells.slice(0, stickyCount)

    // Compute left offsets using measured header widths.
    const offsets = []
    let currentLeft = 0

    headerCells.forEach((cell, idx) => {
      offsets[idx] = currentLeft
      const width = cell.getBoundingClientRect().width
      currentLeft += width
    })

    // Apply offsets to sticky cells in every header row.
    headerRows.forEach(row => {
      const stickyHeaderCells = Array.from(
        row.querySelectorAll("th.fanword-table__sticky-left, td.fanword-table__sticky-left")
      )

      stickyHeaderCells.slice(0, stickyCount).forEach((cell, idx) => {
        cell.style.left = `${offsets[idx]}px`
      })
    })

    // Apply the same offsets to each body row's sticky cells.
    const bodyRows = Array.from(table.querySelectorAll("tbody tr"))
    bodyRows.forEach(row => {
      const stickyBodyCells = Array.from(
        row.querySelectorAll("td.fanword-table__sticky-left")
      )

      stickyBodyCells.slice(0, stickyCount).forEach((cell, idx) => {
        cell.style.left = `${offsets[idx]}px`
      })
    })
  }
}
